import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Tabs } from "@/components/ui/tabs";
import type { TabConfig } from "@/components/ui/tabs";
import { BasicDataTab } from "@/components/athlete-profile/basic-data";
import { WorkoutsTab } from "@/components/athlete-profile/workouts";
import { PhysicalEvaluationTab } from "@/components/athlete-profile/physical-evaluation";
import { PageContainer } from "@/components/ui/page-container";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useAthlete } from "@/queries/use-athletes";
import { useAthleteNameBreadcrumb } from "@/hooks/use-breadcrumb/use-breadcrumb-overrides";

const VALID_TABS = ["basic-data", "physical-evaluation", "workouts"] as const;
type ValidTab = (typeof VALID_TABS)[number];

export const Route = createFileRoute("/(protected)/athletes/$athleteId/")({
  component: RouteComponent,
  validateSearch: (search) => {
    const tab = search.tab as string;
    return {
      tab: (VALID_TABS.includes(tab as ValidTab)
        ? tab
        : "basic-data") as ValidTab,
    };
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const { athleteId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { tab } = Route.useSearch();
  const { data: athlete } = useAthlete(Number(athleteId));

  useAthleteNameBreadcrumb(Number(athleteId));

  const handleTabChange = (newTab: string) => {
    navigate({ search: { tab: newTab as ValidTab } });
  };

  const tabs: TabConfig[] = [
    {
      value: "basic-data",
      label: t("pages:profile.tabs.basicData"),
      content: <BasicDataTab athleteId={Number(athleteId)} />,
    },
    {
      value: "payments",
      label: t("pages:profile.tabs.payments"),
      content: <></>,
      disabled: true,
    },
    {
      value: "statistics",
      label: t("pages:profile.tabs.statistics"),
      content: <></>,
      disabled: true,
    },
    {
      value: "physical-evaluation",
      label: t("pages:profile.tabs.physicalEvaluation"),
      content: <PhysicalEvaluationTab athleteId={Number(athleteId)} />,
    },
    {
      value: "workouts",
      label: t("pages:profile.tabs.workouts"),
      content: <WorkoutsTab athleteId={Number(athleteId)} />,
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mt-6 flex items-center gap-3 px-6 md:hidden">
        {athlete && (
          <>
            <UserAvatar
              variant="table"
              firstName={athlete.firstName}
              lastName={athlete.lastName}
              avatarUrl={athlete.avatarUrl}
            />
            <span className="text-lg font-bold text-gray-800 dark:text-white">
              {athlete.firstName} {athlete.lastName}
            </span>
          </>
        )}
      </div>
      <PageContainer className="mx-6">
        <Tabs tabs={tabs} value={tab} onValueChange={handleTabChange} lazy />
      </PageContainer>
    </div>
  );
}
